import type { GenericGroup, GenericInstance, PolicyProductionFilter } from '@/types/versioning';
import { LifecycleStatus } from '@/types/enums';

type VersionedGroupLike<I extends GenericInstance = GenericInstance> = Pick<
    GenericGroup,
    'current_instance_id' | 'draft_id'
> & {
    instances: I[];
};

/** Preferred instance for UI display: current release, then draft. */
export const getDisplayInstanceId = (group: Pick<GenericGroup, 'current_instance_id' | 'draft_id'>): string | undefined => {
    return group.current_instance_id ?? group.draft_id;
};

/** Resolve the instance matching `getDisplayInstanceId`, if present in `instances`. */
export const getDisplayInstance = <I extends GenericInstance>(group: VersionedGroupLike<I>): I | undefined => {
    const displayInstanceId = getDisplayInstanceId(group);
    if (!displayInstanceId) {
        return undefined;
    }

    return group.instances.find((instance) => instance.id === displayInstanceId);
};

/** Requires the sidebar-selected instance to be a published/archived release (draft source for new draft). */
export function requireSelectedReleaseInstanceId(
    instances: GenericInstance[],
    selectedInstanceId: string | undefined,
): string {
    if (!selectedInstanceId) {
        throw new Error('Selecione uma versão publicada para criar o rascunho.');
    }
    const selected = instances.find((i) => i.id === selectedInstanceId);
    if (!selected) {
        throw new Error('Versão selecionada não encontrada.');
    }
    if (selected.version_instance_lifecycle === LifecycleStatus.DRAFT) {
        throw new Error('Selecione uma versão publicada ou arquivada, não o rascunho.');
    }
    return selected.id;
}

/**
 * History order for the version sidebar: draft first, then released versions newest
 * (highest version_number) first, so the instance a user is most likely to act on
 * sits at the top of "Histórico de Versões".
 */
export function sortInstancesForHistory<I extends GenericInstance>(instances: I[]): I[] {
    const isDraft = (i: I) => i.version_instance_lifecycle === LifecycleStatus.DRAFT;
    return [...instances].sort((a, b) => {
        if (isDraft(a) !== isDraft(b)) return isDraft(a) ? -1 : 1;
        return (b.version_number ?? 0) - (a.version_number ?? 0);
    });
}

/** API `is_active` value for a policy-list production filter; null means "don't filter". */
export const productionFilterToIsActive = (filter: PolicyProductionFilter): boolean | null =>
    filter === 'all' ? null : filter === 'in_production';
